// SignalFormModal.tsx - CORRIGIDO: Sem manipulação direta do DOM
import FeatherIcon from 'feather-icons-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { videoProjectService } from 'services/videoService';

interface FormData {
  service_id: number;
  signal_type: string;
  signal_device_id: number;
  signal_film_id: number;
  signal_support_type: string;
  signal_support_id: number | null;
  signal_regions_id: number | null; // ✅ NOVO
  signal_region_area: string | null; // ✅ NOVO
  comments: string;
  x: number;
  y: number;
  timestamp: number;
  frameNumber: number;
  imageId?: number;
  lat: number;
  long: number;
}

interface Props {
  show: boolean;
  onHide: () => void;
  onSubmit: (data: FormData) => void;
  onDelete?: () => void;
  formData: FormData;
  setFormData: (data: FormData) => void;
  currentImage: { lat: number | null; long: number | null } | null;
  editingIndex: number | null;
  formatTimeWithMilliseconds: (time: number) => string;
  gpsDisplacementEnabled: boolean;
  hasGPSAdjustment: boolean;
  gpsDisplacementResult?: any;
}

// Modal Customizado (sem Bootstrap Modal)
const CustomModal: React.FC<{
  show: boolean;
  onHide: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'lg' | 'xl';
}> = ({ show, onHide, title, children, size = 'lg' }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(1);
  const [showOpacitySlider, setShowOpacitySlider] = useState(false);

  // Reset quando abre/fecha
  useEffect(() => {
    if (show) {
      setPosition({ x: 0, y: 0 });
      setOpacity(1);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [show]);

  // Suporte para fechar com ESC
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && show) {
        e.preventDefault();
        e.stopPropagation();
        onHide();
      }
    };

    if (show) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [show, onHide]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    },
    [position]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        });
      }
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!show) return null;

  const modalWidth =
    size === 'sm' ? '500px' : size === 'xl' ? '1140px' : '800px';

  return (
    <>
      {/* Backdrop */}
      <div
        className="custom-modal-backdrop"
        onClick={onHide}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040,
          display: show ? 'block' : 'none'
        }}
      />

      {/* Modal */}
      <div
        className="custom-modal"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
          width: modalWidth,
          maxWidth: '90vw',
          maxHeight: isDragging ? '85vh' : '90vh',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: isDragging
            ? '0 8px 32px rgba(0,0,0,0.3)'
            : '0 4px 16px rgba(0,0,0,0.15)',
          opacity,
          zIndex: 1050,
          display: show ? 'flex' : 'none',
          flexDirection: 'column',
          transition: isDragging ? 'none' : 'box-shadow 0.2s',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1rem',
            borderBottom: '1px solid #dee2e6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}
        >
          <h5 style={{ margin: 0, flex: 1 }}>{title}</h5>

          {/* Controles */}
          <div
            className="d-flex align-items-center gap-2"
            style={{ marginRight: '8px' }}
          >
            {/* Transparência */}
            <div style={{ position: 'relative' }}>
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowOpacitySlider(!showOpacitySlider)}
                style={{ padding: '4px 8px', color: '#6c757d' }}
              >
                <FeatherIcon icon="eye" size={16} />
                <span style={{ fontSize: '11px', marginLeft: '4px' }}>
                  {Math.round(opacity * 100)}%
                </span>
              </Button>

              {showOpacitySlider && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    padding: '10px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    zIndex: 1100,
                    minWidth: '200px'
                  }}
                >
                  <input
                    type="range"
                    min="30"
                    max="100"
                    value={opacity * 100}
                    onChange={e => setOpacity(parseInt(e.target.value) / 100)}
                    style={{ width: '100%' }}
                  />
                </div>
              )}
            </div>

            {/* Arrastar */}
            <div
              onMouseDown={handleMouseDown}
              style={{
                cursor: isDragging ? 'grabbing' : 'grab',
                padding: '4px 8px',
                backgroundColor: isDragging ? '#e9ecef' : '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #dee2e6',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                userSelect: 'none'
              }}
            >
              <FeatherIcon icon="move" size={14} />
              <span style={{ fontSize: '11px' }}>
                {isDragging ? 'Arrastando...' : 'Arrastar'}
              </span>
            </div>

            {/* Fechar */}
            <button
              onClick={onHide}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                padding: '0 4px',
                color: '#6c757d'
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          style={{
            padding: '1rem',
            flex: 1,
            overflowY: isDragging ? 'hidden' : 'auto',
            overflowX: 'hidden'
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
};

// Componente GPS Indicator
const GPSAdjustmentIndicator: React.FC<{
  hasAdjustment: boolean;
  displacementResult: any;
  currentImage: { lat: number | null; long: number | null } | null;
  editingIndex?: number | null;
}> = ({ hasAdjustment, displacementResult, currentImage, editingIndex }) => {
  const isMapClick = displacementResult?.source === 'map_click';

  if (hasAdjustment && displacementResult && displacementResult.adjustedGPS) {
    if (isMapClick) {
      return (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#d1f4dd',
            borderRadius: '8px',
            marginBottom: '15px',
            border: '2px solid #28a745'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}
          >
            <span style={{ fontSize: '20px' }}>🗺️</span>
            <div>
              <strong style={{ color: '#155724' }}>GPS do Mapa</strong>
              <p style={{ margin: 0, fontSize: '12px', color: '#155724' }}>
                Posição selecionada diretamente no mapa
              </p>
            </div>
          </div>
          <div
            style={{
              backgroundColor: 'white',
              padding: '8px',
              borderRadius: '6px',
              fontSize: '11px',
              fontFamily: 'monospace'
            }}
          >
            <div style={{ color: '#28a745', wordBreak: 'break-all' }}>
              <strong>Lat:</strong>{' '}
              {displacementResult.adjustedGPS.lat.toFixed(6)}
            </div>
            <div style={{ color: '#28a745', wordBreak: 'break-all' }}>
              <strong>Lng:</strong>{' '}
              {displacementResult.adjustedGPS.lng.toFixed(6)}
            </div>
          </div>
        </div>
      );
    }

    const distance = Math.sqrt(
      Math.pow(displacementResult.horizontalOffset || 0, 2) +
        Math.pow(displacementResult.depthOffset || 0, 2)
    );

    return (
      <div
        style={{
          padding: '12px',
          backgroundColor: '#d1ecf1',
          borderRadius: '8px',
          marginBottom: '15px',
          border: '2px solid #0dcaf0'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
          }}
        >
          <span style={{ fontSize: '20px' }}>🎯</span>
          <div>
            <strong style={{ color: '#055160' }}>GPS Ajustado Ativo!</strong>
            <p style={{ margin: 0, fontSize: '12px', color: '#055160' }}>
              Posição calculada com precisão
            </p>
          </div>
        </div>
        <div
          style={{
            backgroundColor: 'white',
            padding: '8px',
            borderRadius: '6px',
            fontSize: '11px',
            fontFamily: 'monospace'
          }}
        >
          <div>
            Horizontal:{' '}
            <strong>
              {(displacementResult.horizontalOffset || 0).toFixed(2)}m
            </strong>
          </div>
          <div>
            Profundidade:{' '}
            <strong>{(displacementResult.depthOffset || 0).toFixed(2)}m</strong>
          </div>
          <div>
            Distância Total: <strong>{distance.toFixed(2)}m</strong>
          </div>
          <hr style={{ margin: '8px 0' }} />
          <div style={{ color: '#0dcaf0' }}>
            <strong>Lat:</strong>{' '}
            {displacementResult.adjustedGPS.lat.toFixed(6)}
            <br />
            <strong>Lng:</strong>{' '}
            {displacementResult.adjustedGPS.lng.toFixed(6)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '10px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        marginBottom: '15px',
        border: '2px dashed #dee2e6'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '20px' }}>📍</span>
        <div style={{ flex: 1 }}>
          <strong style={{ color: '#6c757d' }}>
            GPS {editingIndex !== null ? '(Referência)' : '(Original)'}
          </strong>
          {currentImage &&
            currentImage.lat !== null &&
            currentImage.long !== null && (
              <div
                style={{
                  marginTop: '4px',
                  fontSize: '11px',
                  color: '#6c757d',
                  fontFamily: 'monospace'
                }}
              >
                Lat: {currentImage.lat.toFixed(6)} | Lng:{' '}
                {currentImage.long.toFixed(6)}
              </div>
            )}
          {editingIndex !== null && (
            <div
              style={{
                marginTop: '6px',
                fontSize: '11px',
                color: '#0dcaf0',
                fontStyle: 'italic'
              }}
            >
              ℹ️ A edição não altera a posição GPS no mapa
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Modal Principal
const SignalFormModal: React.FC<Props> = ({
  show,
  onHide,
  onSubmit,
  onDelete,
  formData,
  setFormData,
  currentImage,
  editingIndex,
  formatTimeWithMilliseconds,
  gpsDisplacementEnabled,
  hasGPSAdjustment,
  gpsDisplacementResult: propsGpsDisplacementResult
}) => {
  const [devices, setDevices] = useState<any[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [filteredDevices, setFilteredDevices] = useState<any[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<string[]>([]);
  const [selectedDeviceImage, setSelectedDeviceImage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);

  const [services, setServices] = useState<any[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  const [supportTypes, setSupportTypes] = useState<string[]>([]);
  const [availableSupports, setAvailableSupports] = useState<any[]>([]);
  const [supportsLoading, setSupportsLoading] = useState(false);
  const [regions, setRegions] = useState<any[]>([]);
  const [regionsLoading, setRegionsLoading] = useState(false);

  const [gpsDisplacementResult, setGpsDisplacementResult] = useState<any>(null);

  useEffect(() => {
    if (show) {
      if (propsGpsDisplacementResult) {
        setGpsDisplacementResult(propsGpsDisplacementResult);
      } else if ((window as any).__clickGPSResult) {
        setGpsDisplacementResult((window as any).__clickGPSResult);
      } else {
        setGpsDisplacementResult(null);
      }
    }
  }, [
    show,
    gpsDisplacementEnabled,
    hasGPSAdjustment,
    propsGpsDisplacementResult
  ]);

  // Reset o erro quando mudar a imagem
  useEffect(() => {
    setImageLoadError(false);
  }, [selectedDeviceImage]);

  const films = [
    { id: 1, name: 'Tipo I' },
    { id: 2, name: 'Tipo III' }
  ];

  useEffect(() => {
    if (show) {
      fetchServices();
      fetchDevices();
      fetchSupportTypes();
      fetchRegions();
    }
  }, [show]);

  const fetchServices = async () => {
    setServicesLoading(true);
    try {
      const servicesData = await videoProjectService.getSignalServices();
      console.log('Serviços carregados:', servicesData);
      setServices(servicesData);
    } catch (error) {
      console.error('Erro ao buscar serviços:', error);
      setServices([]);
    } finally {
      setServicesLoading(false);
    }
  };

  const fetchSupportTypes = async () => {
    try {
      const types = await videoProjectService.getSignalSupportTypes();
      setSupportTypes(types);
    } catch (error) {
      console.error('Erro ao buscar tipos de suporte:', error);
      setSupportTypes([]);
    }
  };

  const fetchRegions = async () => {
    setRegionsLoading(true);
    try {
      const regionsData = await videoProjectService.getSignalRegions();
      setRegions(regionsData);
    } catch (error) {
      console.error('Erro ao buscar regiões:', error);
      setRegions([]);
    } finally {
      setRegionsLoading(false);
    }
  };

  useEffect(() => {
    if (formData.signal_support_type) {
      fetchSupportsByType(formData.signal_support_type);
    } else {
      setAvailableSupports([]);
    }
  }, [formData.signal_support_type]);

  const fetchSupportsByType = async (type: string) => {
    setSupportsLoading(true);
    try {
      const supports = await videoProjectService.getSignalSupportsByType(type);
      setAvailableSupports(supports);
    } catch (error) {
      console.error(`Erro ao buscar suportes do tipo "${type}":`, error);
      setAvailableSupports([]);
    } finally {
      setSupportsLoading(false);
    }
  };

  const fetchDevices = async () => {
    setDevicesLoading(true);
    try {
      const devicesData = await videoProjectService.getSignalDevices();
      setDevices(devicesData);

      const types = [...new Set(devicesData.map((d: any) => d.type))].sort();
      setDeviceTypes(types);

      if (editingIndex !== null && formData.signal_type) {
        const filtered = devicesData.filter(
          (d: any) => d.type === formData.signal_type
        );
        setFilteredDevices(filtered);

        const selectedDevice = devicesData.find(
          (d: any) => d.id === formData.signal_device_id
        );
        if (selectedDevice) {
          setSelectedDeviceImage(`/images/signs/${selectedDevice.symbol_name}`);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar dispositivos:', error);
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const type = e.target.value;
    setFormData({
      ...formData,
      signal_type: type,
      signal_device_id: 0
    });
    setSelectedDeviceImage('');
    setImageLoadError(false);
    const filtered = devices.filter(d => d.type === type);
    setFilteredDevices(filtered);
  };

  const handleDeviceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const deviceId = parseInt(e.target.value);
    const device = devices.find(d => d.id === deviceId);
    setFormData({
      ...formData,
      signal_device_id: deviceId
    });
    if (device) {
      setSelectedDeviceImage(`/images/signs/${device.symbol_name}`);
      setImageLoadError(false);
    } else {
      setSelectedDeviceImage('');
    }
  };

  const handleSupportTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const type = e.target.value;
    setFormData({
      ...formData,
      signal_support_type: type,
      signal_support_id: null
    });
  };

  const formatTypeName = (type: string) => {
    const typeNames: { [key: string]: string } = {
      regulamentacao: 'Regulamentação',
      advertencia: 'Advertência',
      auxiliares: 'Auxiliares',
      indicativa: 'Indicativa',
      aereo: 'Aéreo',
      solo: 'Solo'
    };
    return typeNames[type] || type;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(false);
    if (onDelete) {
      onDelete();
    }
    onHide();
  };

  const isMapClickGPS = gpsDisplacementResult?.source === 'map_click';
  const shouldShowAdjustedGPS =
    (gpsDisplacementEnabled || isMapClickGPS) &&
    gpsDisplacementResult &&
    gpsDisplacementResult.adjustedGPS;

  return (
    <>
      <CustomModal
        show={show}
        onHide={onHide}
        size="lg"
        title={
          editingIndex !== null ? 'Editar Sinalização' : 'Cadastrar Sinalização'
        }
      >
        <GPSAdjustmentIndicator
          hasAdjustment={shouldShowAdjustedGPS}
          displacementResult={gpsDisplacementResult}
          currentImage={currentImage}
          editingIndex={editingIndex}
        />

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Serviço *</Form.Label>
                <Form.Select
                  value={formData.service_id || ''}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      service_id: parseInt(e.target.value)
                    })
                  }
                  required
                  disabled={servicesLoading}
                >
                  <option value="">
                    {servicesLoading ? 'Carregando...' : 'Selecione...'}
                  </option>
                  {services.map(service => (
                    <option
                      key={service.id}
                      value={service.id}
                      title={service.description || ''}
                    >
                      {service.name}
                    </option>
                  ))}
                </Form.Select>
                {formData.service_id > 0 &&
                  services.find(s => s.id === formData.service_id)
                    ?.description && (
                    <Form.Text className="text-muted">
                      {
                        services.find(s => s.id === formData.service_id)
                          ?.description
                      }
                    </Form.Text>
                  )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Tipo de Sinalização *</Form.Label>
                <Form.Select
                  value={formData.signal_type || ''}
                  onChange={handleTypeChange}
                  required
                  disabled={devicesLoading}
                >
                  <option value="">
                    {devicesLoading ? 'Carregando...' : 'Selecione...'}
                  </option>
                  {deviceTypes.map(type => (
                    <option key={type} value={type}>
                      {formatTypeName(type)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Código da Placa *</Form.Label>
                <Form.Select
                  value={formData.signal_device_id || ''}
                  onChange={handleDeviceChange}
                  required
                  disabled={
                    !formData.signal_type || filteredDevices.length === 0
                  }
                >
                  <option value="">Selecione...</option>
                  {filteredDevices.map(device => (
                    <option key={device.id} value={device.id}>
                      {device.code} - {device.name}
                    </option>
                  ))}
                </Form.Select>
                {formData.signal_device_id > 0 && (
                  <Form.Text className="text-muted">
                    {
                      devices.find(d => d.id === formData.signal_device_id)
                        ?.description
                    }
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Película *</Form.Label>
                <Form.Select
                  value={formData.signal_film_id || ''}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      signal_film_id: parseInt(e.target.value)
                    })
                  }
                  required
                >
                  <option value="">Selecione...</option>
                  {films.map(film => (
                    <option key={film.id} value={film.id}>
                      {film.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Tamanho da Placa *</Form.Label>
                <Form.Select
                  value={formData.signal_regions_id || ''}
                  onChange={e => {
                    const regionId =
                      e.target.value === '' ? null : parseInt(e.target.value);
                    setFormData({
                      ...formData,
                      signal_regions_id: regionId,
                      signal_region_area:
                        regionId === 3 ? formData.signal_region_area : null
                    });
                  }}
                  required
                  disabled={regionsLoading}
                >
                  <option value="">
                    {regionsLoading ? 'Carregando...' : 'Selecione...'}
                  </option>
                  {regions.map(region => (
                    <option key={region.id} value={region.id}>
                      {region.name}
                    </option>
                  ))}
                </Form.Select>
                {formData.signal_regions_id &&
                  regions.find(r => r.id === formData.signal_regions_id)
                    ?.description && (
                    <Form.Text className="text-muted">
                      {
                        regions.find(r => r.id === formData.signal_regions_id)
                          ?.description
                      }
                    </Form.Text>
                  )}
              </Form.Group>

              {formData.signal_regions_id === 3 && (
                <Form.Group className="mb-3">
                  <Form.Label>Especificar Tamanho *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.signal_region_area || ''}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        signal_region_area: e.target.value
                      })
                    }
                    placeholder="Ex: 10m², 50cm x 70cm, etc."
                    required
                  />
                  <Form.Text className="text-muted">
                    Informe o tamanho ou área da placa
                  </Form.Text>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Tipo de Suporte</Form.Label>
                <Form.Select
                  value={formData.signal_support_type || ''}
                  onChange={handleSupportTypeChange}
                >
                  <option value="">Sem suporte / Selecione...</option>
                  {supportTypes.map(type => (
                    <option key={type} value={type}>
                      {formatTypeName(type)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Suporte</Form.Label>
                <Form.Select
                  value={formData.signal_support_id || ''}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      signal_support_id:
                        e.target.value === '' ? null : parseInt(e.target.value)
                    })
                  }
                  disabled={!formData.signal_support_type || supportsLoading}
                >
                  <option value="">
                    {supportsLoading
                      ? 'Carregando...'
                      : formData.signal_support_type
                      ? 'Selecione...'
                      : 'Selecione um tipo primeiro'}
                  </option>
                  {availableSupports.map(support => (
                    <option key={support.id} value={support.id}>
                      {support.code} - {support.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Observações</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.comments || ''}
                  onChange={e =>
                    setFormData({ ...formData, comments: e.target.value })
                  }
                  placeholder="Adicione informações adicionais..."
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <div className="text-center mb-3">
                <small className="text-muted d-block mb-2">
                  Imagem da Sinalização
                </small>
                {selectedDeviceImage && !imageLoadError ? (
                  <div
                    className="border rounded p-3 d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: '150px',
                      height: '150px',
                      backgroundColor: '#f8f9fa'
                    }}
                  >
                    <img
                      src={selectedDeviceImage}
                      alt="Sinalização"
                      style={{
                        maxWidth: '120px',
                        maxHeight: '120px',
                        objectFit: 'contain'
                      }}
                      onError={() => setImageLoadError(true)}
                    />
                  </div>
                ) : imageLoadError ? (
                  <div
                    className="border rounded p-3 d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: '150px',
                      height: '150px',
                      backgroundColor: '#f8f9fa'
                    }}
                  >
                    <div style={{ fontSize: '50px' }}>🚦</div>
                  </div>
                ) : (
                  <div
                    className="border rounded p-3 d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: '150px',
                      height: '150px',
                      backgroundColor: '#f8f9fa'
                    }}
                  >
                    <small className="text-muted">Selecione uma placa</small>
                  </div>
                )}
              </div>

              <div className="p-3 bg-light rounded">
                <h6 className="mb-2">Informações do Ponto</h6>
                <small className="d-block">
                  <strong>Coordenadas X,Y:</strong> {formData.x.toFixed(3)},{' '}
                  {formData.y.toFixed(3)}
                </small>
                <small className="d-block">
                  <strong>Frame:</strong> {formData.frameNumber}
                </small>
                <small className="d-block">
                  <strong>Tempo:</strong>{' '}
                  {formatTimeWithMilliseconds(formData.timestamp)}
                </small>
                {shouldShowAdjustedGPS ? (
                  <small className="d-block">
                    <strong>
                      GPS ({isMapClickGPS ? 'Mapa' : 'Ajustado'}):
                    </strong>{' '}
                    {gpsDisplacementResult.adjustedGPS.lat.toFixed(6)},{' '}
                    {gpsDisplacementResult.adjustedGPS.lng.toFixed(6)}
                  </small>
                ) : (
                  currentImage &&
                  currentImage.lat !== null &&
                  currentImage.long !== null && (
                    <>
                      <small className="d-block">
                        <strong>
                          GPS{' '}
                          {editingIndex !== null
                            ? '(Referência)'
                            : '(Original)'}
                          :
                        </strong>{' '}
                        {currentImage.lat.toFixed(6)},{' '}
                        {currentImage.long.toFixed(6)}
                      </small>
                      {editingIndex !== null && (
                        <small className="d-block text-muted fst-italic mt-1">
                          ℹ️ A edição não altera a posição GPS no mapa
                        </small>
                      )}
                    </>
                  )
                )}
              </div>
            </Col>
          </Row>

          <hr />

          <div className="d-flex justify-content-between">
            {editingIndex !== null && onDelete && (
              <Button
                variant="outline-danger"
                onClick={() => setShowDeleteConfirm(true)}
                type="button"
              >
                <FeatherIcon icon="trash-2" size={16} className="me-1" />
                Excluir
              </Button>
            )}
            <div className="ms-auto d-flex gap-2">
              <Button variant="secondary" onClick={onHide} type="button">
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  !formData.service_id ||
                  !formData.signal_device_id ||
                  !formData.signal_film_id ||
                  !formData.signal_regions_id ||
                  (formData.signal_regions_id === 3 &&
                    !formData.signal_region_area) ||
                  devicesLoading ||
                  supportsLoading ||
                  regionsLoading
                }
              >
                <FeatherIcon icon="save" size={16} className="me-1" />
                {editingIndex !== null ? 'Salvar Alterações' : 'Cadastrar'}
              </Button>
            </div>
          </div>
        </Form>
      </CustomModal>

      {/* Modal de confirmação de exclusão */}
      <CustomModal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        size="sm"
        title="Confirmar Exclusão"
      >
        <p>Tem certeza que deseja excluir esta sinalização?</p>
        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button
            variant="secondary"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Excluir
          </Button>
        </div>
      </CustomModal>
    </>
  );
};

export default SignalFormModal;
